import React, { useEffect, useRef, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../../firebase';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { getGraphFlagWrite } from '../utils/emailFlagState';
import { KTSVG } from '../../../../_metronic/helpers'; // Assuming KTSVG is accessible here
import { useEmailPanel, PanelEmailData } from '../contexts/EmailPanelContext';
import { parseAddresses } from '../utils/replyRecipients';
import EmailFeedContent from './EmailFeedContent'; // Import the new component
import TerminalEmailFeed from './TerminalEmailFeed'; // Terminal-theme replacement for the feed
import EmailView from './EmailView'; // Import the new component
import TerminalEmailView from './TerminalEmailView'; // Terminal-theme replacement
import SendEmailForm from './SendEmailForm'; // Import the new component
import type { SendEmailFormRef } from './SendEmailForm'; // Canonical ref type
import TerminalSendEmailForm from '../../job-summary/workspace/TerminalSendEmailForm';
import type { TerminalSendEmailFormRef } from '../../job-summary/workspace/TerminalSendEmailForm';
import DraftsPanel from './DraftsPanel'; // Import the drafts panel component
import { useTerminalTheme } from '../../../hooks/useTerminalTheme';
import { useGraphiteTheme } from '../../../hooks/useGraphiteTheme';
import { syncPushTokenIfGranted } from '../../../utils/pushNotifications';
import PushOptIn from './PushOptIn'; // "notify me when a client emails" strip (renders nothing unless offerable)
import { useAppBadge } from '../hooks/useAppBadge';
import { useOpenEmailDeepLink } from '../hooks/useOpenEmailDeepLink';
import { ReplyIcon } from './icons/ReplyIcon'; // Import new icon
import { ForwardIcon } from './icons/ForwardIcon'; // Import new icon
import { ReplyAllIcon } from './icons/ReplyAllIcon'; // Import new icon

/** Both classic and terminal composer forms expose `checkForUnsavedChanges`,
 *  which is the only method this panel calls on the imperative handle. We
 *  track them via separate typed refs — one gets attached, the other stays
 *  null — and resolve the active one at call time. */
type ComposerFormRef = SendEmailFormRef;

/** Local extension of PanelEmailData to carry the Reply All flag without modifying the context file */
type ComposerEmailData = PanelEmailData & { isReplyAll?: boolean };

// Graph action endpoints — mirror the per-row actions in the feed so an OPEN
// email gets the same junk / delete / flag / read controls.
const CF = 'https://us-central1-web-construct-app.cloudfunctions.net';
const TOGGLE_GRAPH_JUNK_URL = `${CF}/toggleGraphJunk`;
const DELETE_GRAPH_EMAIL_URL = `${CF}/deleteGraphEmail`;
const SET_GRAPH_FLAG_URL = `${CF}/setGraphEmailFlagState`;
const SET_GRAPH_READ_URL = `${CF}/setGraphEmailReadState`;

// Utility functions to handle reply and forward subjects properly
const formatReplySubject = (originalSubject: string | undefined): string => {
  if (!originalSubject) return 'Re: ';
  
  // Check if subject already starts with "Re:" (case insensitive)
  const trimmedSubject = originalSubject.trim();
  if (trimmedSubject.toLowerCase().startsWith('re:')) {
    return trimmedSubject; // Return as-is if already has Re:
  }
  
  return `Re: ${trimmedSubject}`;
};

const formatForwardSubject = (originalSubject: string | undefined): string => {
  if (!originalSubject) return 'Fwd: ';
  
  // Check if subject already starts with "Fwd:" or "FWD:" (case insensitive)
  const trimmedSubject = originalSubject.trim();
  if (trimmedSubject.toLowerCase().startsWith('fwd:')) {
    return trimmedSubject; // Return as-is if already has Fwd:
  }
  
  return `Fwd: ${trimmedSubject}`;
};

export type ReplyMode = 'reply' | 'replyAll';

/**
 * The source email with `from` guaranteed to be an ARRAY of {address,name}.
 *
 * Why: legacy docs can carry `from` as a bare string ("Client <c@x.com>") or a
 * single {address,name} object (TerminalEmailView.getDisplayFromList supports
 * both, so they render and Reply stays enabled), and they reach this panel raw
 * via the deep-link / event-log fallbacks. Both composers `.map` `from` inside
 * an effect: handing them a non-array used to die in the click handler (a dead
 * button) and, now that the original email is forwarded untouched, would throw
 * in the composer's effect instead and unmount the whole app. An array `from`
 * (every normal doc) - and a missing one - passes through untouched, so nothing
 * changes for the shapes that already worked.
 */
function withArrayFrom(source: PanelEmailData): PanelEmailData {
  const from: unknown = source.from;
  if (from === null || from === undefined || Array.isArray(from)) return source;
  return { ...source, from: parseAddresses(from as Parameters<typeof parseAddresses>[0]) };
}

/**
 * Composer initialData for Reply / Reply All: the ORIGINAL email, untouched
 * (to, cc, from, folder, emailType, direction, senderEmail, mailbox, deleted,
 * graph, source ...) plus the reply flags and the "Re:" subject.
 *
 * Why the recipients are NOT computed here: whether the owner sent this email
 * decides who the reply goes to (their own sent mail must go to the original
 * recipients, not back to their own address), and that decision needs signals
 * only the composer has (send accounts, signed-in user). The composers work it
 * out once, via replyRecipients. This builder used to overwrite `to` with the
 * from address (and `cc` with a comma-joined string), which for an email the
 * owner sent meant replying to themselves - and left the composer no way to
 * tell, because the real recipients were already gone.
 */
export function buildReplyData(source: PanelEmailData, mode: ReplyMode): ComposerEmailData {
  return {
    ...withArrayFrom(source),
    isReply: true,
    isReplyAll: mode === 'replyAll',
    subject: formatReplySubject(source.subject),
  };
}

// Placeholder imports - we will create/move these components later
// import EmailView from './EmailView';
// import SendEmailForm from './SendEmailForm';

// Temporary Placeholder Components
// const PlaceholderFeed: React.FC = () => <div className='p-5'>Email Feed Content (Placeholder)</div>;
const PlaceholderViewEmail: React.FC<{ emailData: PanelEmailData | null }> = ({ emailData }) => (
  <div className='p-5'>
    View Email Content (Placeholder)
    <p>Subject: {emailData?.subject || 'N/A'}</p>
  </div>
);
const PlaceholderComposeEmail: React.FC<{ initialData?: PanelEmailData | null }> = ({ initialData }) => (
  <div className='p-5'>
    Compose Email Form (Placeholder)
    {initialData?.isReply && <p>Replying to: {initialData?.subject}</p>}
    {initialData?.to && <p>To: {Array.isArray(initialData.to) ? initialData.to.join(', ') : initialData.to}</p>}
  </div>
);

const EmailSlidingPanel: React.FC = () => {
  const {
    isEmailPanelOpen,
    emailPanelView, // Global intended view (feed, viewEmail, composeEmail)
    currentEmailData, // Data associated with the global intended view
    closeEmailPanel,
    setEmailPanelView, // Global context function to change view
    setDraftMode,
    currentDraft, // The saved draft opened for editing (draftMode 'edit')
    unreadBadgeCount,
    isLoadingInitialEmails,
  } = useEmailPanel();

  // Push notifications (new client email). All three run while the panel is
  // closed too - this component stays mounted for the signed-in session, and
  // a notification tap must be able to open the panel from nothing.
  //  - deep link: '/?openEmail=<id>' or the service worker's OPEN_EMAIL message
  //  - app icon badge mirrors the unread count (skipped until the first
  //    snapshot, which reports 0 and would wipe the badge the worker set)
  //  - refresh/re-register the FCM token when permission is already granted
  useOpenEmailDeepLink();
  useAppBadge(unreadBadgeCount, !isLoadingInitialEmails);
  useEffect(() => { void syncPushTokenIfGranted(); }, []);

  // Bridge: the global Approvals pane lives OUTSIDE EmailPanelProvider, so it can't
  // call openEmailPanel directly. It dispatches an 'approvals:open-draft' window
  // event with the draft; here (inside the provider) we open the composer to edit
  // and send it — the same path DraftsPanel uses (review-and-send, never auto-send).
  useEffect(() => {
    const handler = (e: Event) => {
      const draft = (e as CustomEvent).detail?.draft;
      if (!draft) return;
      setDraftMode('edit', draft);
      setEmailPanelView('editDraft');
    };
    window.addEventListener('approvals:open-draft', handler as EventListener);
    return () => window.removeEventListener('approvals:open-draft', handler as EventListener);
  }, [setDraftMode, setEmailPanelView]);

  // Active theme — swap in the terminal-native feed/view/composer when the user
  // has the `terminal` OR `graphite` theme applied (graphite re-skins the
  // terminal-native components warm + Inter via mail.scss; the classic
  // EmailFeedContent/EmailView/SendEmailForm are kept for other themes).
  const isTerminalTheme = useTerminalTheme();
  const isGraphite = useGraphiteTheme();
  const useTerminalComponents = isTerminalTheme || isGraphite;

  // Internal state for managing the two-column layout
  const [isComposerColumnVisible, setIsComposerColumnVisible] = useState(false);
  const [composerDataForForm, setComposerDataForForm] = useState<ComposerEmailData | null>(null);
  
  const [mainContentComponent, setMainContentComponent] = useState<'feed' | 'viewEmail' | 'drafts'>('feed');
  const [mainContentData, setMainContentData] = useState<PanelEmailData | null>(null);
  const composerFormRef = React.useRef<ComposerFormRef>(null);
  const terminalComposerFormRef = React.useRef<TerminalSendEmailFormRef>(null);

  // ── Open-email action menu (parity with the feed's per-row 3-dot menu) ──
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // Close the menu on outside click / touch (iOS never synthesizes mousedown
  // for scroll gestures, which left the menu dangling open on phones).
  useEffect(() => {
    if (!actionMenuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) setActionMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [actionMenuOpen]);
  // Always close the menu when the viewed email changes / panel content swaps.
  useEffect(() => { setActionMenuOpen(false); }, [mainContentData, mainContentComponent]);

  const viewedEmailId = (): string =>
    String((mainContentData as any)?.emailId || mainContentData?.id || '');
  const viewedIsGraph = (): boolean =>
    (mainContentData as any)?.source === 'graph' || !!(mainContentData as any)?.graph?.id;

  // Mark the OPEN email as junk → move to Junk in Outlook + reflect in Firestore,
  // then close the panel (it's left the inbox).
  const handleViewedMarkJunk = async () => {
    const key = viewedEmailId();
    if (!key || actionBusy) return;
    // Graph won't move sent items/drafts into Junk (returns 500) — same gate
    // the feed applies, so the viewed-email menu can't fail mid-flight.
    const vFolder = (mainContentData as any)?.folder;
    const vIsSent = vFolder === 'sent' || (mainContentData as any)?.emailType === 'sent';
    if (vIsSent || vFolder === 'drafts') {
      toast.error(`can't mark ${vIsSent ? 'sent' : 'draft'} items as junk`);
      return;
    }
    setActionBusy(true);
    try {
      await updateDoc(doc(db, 'emails', key), { folder: 'junk', quarantined: true, updatedAt: serverTimestamp() });
      if (viewedIsGraph()) {
        const resp = await authFetch(TOGGLE_GRAPH_JUNK_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailId: key, moveToJunk: true }),
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          // Restore where the mail WAS: a hard-coded 'inbox' would drag mail the
          // owner had filed into an Outlook folder (archive/filed) back into the inbox.
          await updateDoc(doc(db, 'emails', key), { folder: (typeof vFolder === 'string' && vFolder) || 'inbox', quarantined: false });
          throw new Error(data.error || `HTTP ${resp.status}`);
        }
      }
      toast.success('Moved to Junk.');
      setActionMenuOpen(false);
      closeEmailPanel();
    } catch (e: any) {
      toast.error(`Couldn't mark as junk: ${e?.message || 'error'}`);
    } finally { setActionBusy(false); }
  };

  // Delete the OPEN email → move to Deleted Items in Outlook + Firestore, close panel.
  const handleViewedDelete = async () => {
    const key = viewedEmailId();
    if (!key || actionBusy) return;
    setActionBusy(true);
    try {
      if (viewedIsGraph()) {
        const resp = await authFetch(DELETE_GRAPH_EMAIL_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailId: key }),
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${resp.status}`);
        }
      }
      await updateDoc(doc(db, 'emails', key), { deleted: true, folder: 'deleted' });
      toast.success('Moved to Deleted Items.');
      setActionMenuOpen(false);
      closeEmailPanel();
    } catch (e: any) {
      toast.error(`Couldn't delete: ${e?.message || 'error'}`);
    } finally { setActionBusy(false); }
  };

  // Toggle the follow-up flag on the open email.
  const handleViewedToggleFlag = async () => {
    const key = viewedEmailId();
    if (!key || actionBusy) return;
    const desired = !((mainContentData as any)?.pinned);
    setActionBusy(true);
    try {
      const optimisticWrite = viewedIsGraph() ? getGraphFlagWrite(desired) : { pinned: desired };
      await updateDoc(doc(db, 'emails', key), optimisticWrite);
      if (viewedIsGraph()) {
        const resp = await authFetch(SET_GRAPH_FLAG_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailId: key, flagged: desired }),
        });
        if (!resp.ok) { await updateDoc(doc(db, 'emails', key), getGraphFlagWrite(!desired)); throw new Error(`HTTP ${resp.status}`); }
      }
      setMainContentData((prev) => (prev ? ({ ...prev, pinned: desired } as PanelEmailData) : prev));
      toast.success(desired ? 'Flagged.' : 'Unflagged.');
      setActionMenuOpen(false);
    } catch (e: any) {
      toast.error(`Couldn't update flag: ${e?.message || 'error'}`);
    } finally { setActionBusy(false); }
  };

  // Mark the OPEN email as unread (so it re-surfaces in the inbox/badge).
  const handleViewedMarkUnread = async () => {
    const key = viewedEmailId();
    if (!key || actionBusy) return;
    setActionBusy(true);
    try {
      await updateDoc(doc(db, 'emails', key), { dismissed: false, isRead: false });
      if (viewedIsGraph()) {
        const resp = await authFetch(SET_GRAPH_READ_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailId: key, isRead: false }),
        });
        if (!resp.ok) { await updateDoc(doc(db, 'emails', key), { dismissed: true, isRead: true }); throw new Error(`HTTP ${resp.status}`); }
      }
      toast.success('Marked unread.');
      setActionMenuOpen(false);
      closeEmailPanel();
    } catch (e: any) {
      toast.error(`Couldn't mark unread: ${e?.message || 'error'}`);
    } finally { setActionBusy(false); }
  };

  // Simplified composer animation state
  const [shouldRenderComposer, setShouldRenderComposer] = useState(false);
  const [shouldRenderComposerContent, setShouldRenderComposerContent] = useState(false);
  
  useEffect(() => {
    if (isComposerColumnVisible) {
      setShouldRenderComposer(true);
      // Delay content rendering to avoid conflicts during opening
      const contentTimeout = setTimeout(() => setShouldRenderComposerContent(true), 50);
      return () => clearTimeout(contentTimeout);
    } else {
      // Immediately stop rendering content to prevent ResizeObserver loops
      setShouldRenderComposerContent(false);
      // Delay unmounting the container to allow for close animation
      const timeout = setTimeout(() => setShouldRenderComposer(false), 450);
      return () => clearTimeout(timeout);
    }
  }, [isComposerColumnVisible]);

  // Responsive behavior is now handled entirely by CSS media queries

  // Effect to react to global context changes from useEmailPanel()
  useEffect(() => {
    if (!isEmailPanelOpen) return;

    if (emailPanelView === 'composeEmail' || emailPanelView === 'editDraft') {
      setComposerDataForForm(currentEmailData || {}); // Pass data or empty for new compose
      setIsComposerColumnVisible(true);
      // Keep the main panel as is - don't change it when composing
    } else if (emailPanelView === 'viewEmail') {
      setMainContentComponent('viewEmail');
      setMainContentData(currentEmailData);
      // If a new email is viewed, should it close an open composer? For now, no.
    } else if (emailPanelView === 'feed') {
      setMainContentComponent('feed');
      setMainContentData(null);
      // If feed is explicitly selected, maybe close composer? For now, no.
    } else if (emailPanelView === 'drafts') {
      setMainContentComponent('drafts');
      setMainContentData(null);
    }
  }, [emailPanelView, currentEmailData, isEmailPanelOpen]);

  // Static CSS styles (theme via CSS variables for dark mode consistency)
  const inlineStyles = (
    <style>{`
      .email-sliding-panel {
        position: fixed;
        top: 0;
        right: 0;
        /* dvh: 100vh is the LARGE viewport on iOS Safari — with the address
           bar expanded it cut the bottom ~60-100px (reply bar, last rows)
           behind browser chrome. vh stays as the fallback. */
        height: 100vh;
        height: 100dvh;
        background-color: var(--bs-body-bg);
        box-shadow: -5px 0 15px rgba(0,0,0,0.2);
        z-index: 1050;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        transition: width 0.45s cubic-bezier(0.77, 0, 0.175, 1), transform 0.45s cubic-bezier(0.77, 0, 0.175, 1), opacity 0.35s cubic-bezier(0.77, 0, 0.175, 1);
        opacity: 1;
        will-change: width, transform, opacity;
        width: 50vw;
        min-width: 320px;
        max-width: 100vw;
        padding-left: 0;
        /* Notched phones (the app ships viewport-fit=cover). */
        padding-top: env(safe-area-inset-top);
        padding-right: env(safe-area-inset-right);
        box-sizing: border-box;
      }
      .email-sliding-panel.composer-open {
        width: 100vw;
        min-width: 100vw;
      }
      .email-sliding-panel.closed {
        transform: translateX(100%);
        opacity: 0;
        pointer-events: none;
      }
      .email-sliding-panel.open {
        transform: translateX(0);
        opacity: 1;
        pointer-events: auto;
      }
      .email-panel-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.5);
        z-index: 1040;
        opacity: 1;
        transition: opacity 0.35s cubic-bezier(0.77, 0, 0.175, 1);
        /* A swipe on the backdrop must not scroll the app behind it. */
        overscroll-behavior: none;
        touch-action: none;
      }
      .email-panel-backdrop.closed {
        opacity: 0;
        pointer-events: none;
      }
      .panel-body-flex {
        flex-grow: 1;
        display: flex;
        flex-direction: row;
        overflow: hidden;
        position: relative;
        width: 100%;
        height: 100%;
      }
      .feed-panel {
        min-width: 0;
        overflow-y: auto;
        background: inherit;
        transition: width 0.45s cubic-bezier(0.77, 0, 0.175, 1);
        width: 100%;
        /* Rubber-banding past the end must not scroll the page behind the
           fixed overlay (classic janky-overlay tell on iOS). */
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
      }
      .feed-panel.composer-open {
        width: 50vw;
      }
      .composer-panel-side {
        min-width: 0;
        height: 100%;
        background-color: var(--bs-body-bg);
        box-shadow: -5px 0 15px rgba(0,0,0,0.2);
        z-index: 1060;
        display: flex;
        flex-direction: column;
        position: relative;
        transition: transform 0.45s cubic-bezier(0.77, 0, 0.175, 1), opacity 0.35s cubic-bezier(0.77, 0, 0.175, 1), width 0.45s cubic-bezier(0.77, 0, 0.175, 1);
        opacity: 1;
        will-change: transform, opacity, width;
        width: 0;
        transform: translateX(100%);
      }
      .composer-panel-side.open {
        width: 50vw;
        transform: translateX(0);
        opacity: 1;
        pointer-events: auto;
      }
      .composer-panel-side.closed {
        opacity: 0;
        pointer-events: none;
      }
      @media (max-width: 767.98px) {
        .email-sliding-panel, .email-sliding-panel.composer-open {
          width: 100vw !important;
          min-width: 0;
          max-width: 100vw;
          padding-left: 0 !important;
        }
        .feed-panel {
          width: 100vw !important;
          min-width: 0;
          max-width: 100vw;
        }
        .feed-panel.composer-open {
          display: none !important;
        }
        /* Full-screen composer: absolutely positioned over the body (so its
           fixed width can't squash the feed in the flex row) and animate
           TRANSFORM only — animating width 0→100vw forced layout every frame
           while Tiptap mounted (visible jank on phone CPUs). */
        .composer-panel-side {
          position: absolute;
          inset: 0;
          width: 100vw !important;
          min-width: 0;
          max-width: 100vw;
          transition: transform 0.3s cubic-bezier(0.77, 0, 0.175, 1), opacity 0.25s ease;
        }
        .composer-panel-side.open { transform: translateX(0); }
        .composer-panel-side.closed { transform: translateX(100%); }
        /* With the composer up, the outer panel header (title + actions for
           the now-hidden email) is dead chrome — the composer has its own.
           !important: the header div carries Bootstrap's .d-flex, which is
           display:flex !important — a bare display:none silently loses. */
        .email-sliding-panel.composer-open .panel-main-header { display: none !important; }
        /* Same on the FEED view: the header's only function there was the ✕,
           which now lives in the feed's own topbar — reclaim the row. The
           header stays for viewEmail (reply/back actions) and drafts (back). */
        .email-sliding-panel.feed-view .panel-main-header { display: none !important; }

        /* Touch sizing for the Metronic header/menu controls (they compute to
           ~32px at the 12px mobile root font). */
        .email-sliding-panel .btn.btn-icon.btn-sm { height: 40px; width: 40px; }
        .email-sliding-panel .panel-viewed-menu button { min-height: 44px; }

        /* iOS zooms any focused field under 16px — covers the DraftsPanel
           search/sort and any other Bootstrap fields inside the panel. */
        .email-sliding-panel .form-control,
        .email-sliding-panel .form-select,
        .email-sliding-panel textarea { font-size: 16px; }

        /* Drafts list cards: real touch targets + no sticky-hover lift. */
        .email-sliding-panel .draft-card .btn.btn-icon.btn-sm { height: 40px; width: 40px; }
      }
      /* Draft cards: hover lift only where hover exists; taps get a press
         tint instead (sticky-hover left cards permanently raised on touch). */
      .draft-card { -webkit-tap-highlight-color: transparent; }
      @media (hover: hover) {
        .draft-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
      }
      .draft-card:active { background: rgba(var(--bs-primary-rgb), 0.06); }
    `}</style>
  );

  // Only render panel if open or closing (for animation)
  const [shouldRender, setShouldRender] = useState(false);
  const [isActuallyOpen, setIsActuallyOpen] = useState(false);
  useEffect(() => {
    if (isEmailPanelOpen) {
      setShouldRender(true);
      // Wait a tick before setting open to trigger transition
      const openTimeout = setTimeout(() => setIsActuallyOpen(true), 10);
      return () => clearTimeout(openTimeout);
    } else {
      setIsActuallyOpen(false); // Remove open class immediately
      // Wait for animation to finish before unmounting
      const timeout = setTimeout(() => setShouldRender(false), 450);
      return () => clearTimeout(timeout);
    }
  }, [isEmailPanelOpen]);

  // Lock the page behind the overlay while it's open — without this, iOS
  // chains overscroll to the app underneath, which silently scrolls away.
  // Touch contexts only: on desktop, removing the scrollbar shifts the whole
  // app ~15px on every open/close.
  useEffect(() => {
    if (!isEmailPanelOpen) return;
    if (!window.matchMedia?.('(pointer: coarse)').matches) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isEmailPanelOpen]);

  // Back gesture closes the panel instead of navigating the SPA away — on a
  // phone the panel covers the whole screen (no visible backdrop to tap), and
  // the hardware/edge back gesture is the #1 dismissal habit. We push one
  // history entry on open; popstate closes the panel; closing via the X pops
  // our entry so history stays balanced.
  const pushedHistoryRef = useRef(false);
  useEffect(() => {
    if (isEmailPanelOpen) {
      if (!pushedHistoryRef.current) {
        try { window.history.pushState({ emailPanel: true }, ''); pushedHistoryRef.current = true; } catch { /* ignore */ }
      }
      const onPop = () => {
        pushedHistoryRef.current = false;
        // Same unsaved-changes guard as the backdrop: the classic (CKEditor)
        // composer doesn't autosave — Back must not silently discard its
        // work. Re-push our entry and let its own modal take over.
        if (isComposerColumnVisible && !useTerminalComponents) {
          const activeRef = composerFormRef.current;
          if (activeRef && activeRef.checkForUnsavedChanges()) {
            try { window.history.pushState({ emailPanel: true }, ''); pushedHistoryRef.current = true; } catch { /* ignore */ }
            return;
          }
        }
        closeEmailPanel();
      };
      window.addEventListener('popstate', onPop);
      return () => window.removeEventListener('popstate', onPop);
    }
    // Panel closed by an in-app control while our entry is still on the
    // stack: consume it so the NEXT back gesture doesn't re-fire on a
    // stale entry. Only when our entry is still the TOP of the stack —
    // if the SPA navigated meanwhile, backing would eat that navigation.
    if (pushedHistoryRef.current) {
      pushedHistoryRef.current = false;
      try {
        if ((window.history.state as any)?.emailPanel === true) window.history.back();
      } catch { /* ignore */ }
    }
  }, [isEmailPanelOpen, closeEmailPanel, isComposerColumnVisible, useTerminalComponents]);

  if (!shouldRender) return null;

  // After the composer column closes, point the global view back at whatever
  // the main column is showing. Leaving it at 'editDraft'/'composeEmail' made
  // the NEXT draft-open a state no-op — DraftsPanel sets the same view with no
  // data, React bails on identical state, the open effect never re-fires, and
  // clicking another draft did nothing until the whole panel was closed.
  const resetViewToMainContent = () => {
    if (mainContentComponent === 'drafts') setEmailPanelView('drafts');
    else if (mainContentComponent === 'viewEmail' && mainContentData) setEmailPanelView('viewEmail', mainContentData);
    else setEmailPanelView('feed');
  };

  const handleCloseComposer = () => {
    // The TERMINAL composer autosaves continuously (3s debounce + 30s interval
    // + save-on-unmount), so closing it never loses work — it lands in Drafts,
    // Gmail-style. It also renders NO unsaved-changes modal, so blocking the
    // close on checkForUnsavedChanges() would just wedge the composer open with
    // no way out (and, right after a send, its stale render closure still
    // reports unsaved changes and would block the post-send close). So only the
    // classic CKEditor composer — which pops its OWN Save/Discard/Continue modal
    // from checkForUnsavedChanges() — gets the guard.
    if (!useTerminalComponents) {
      const activeRef = composerFormRef.current;
      if (activeRef && activeRef.checkForUnsavedChanges()) {
        // SendEmailForm shows its own modal for unsaved changes.
        return;
      }
    }

    setIsComposerColumnVisible(false);
    setComposerDataForForm(null);
    // Clear the global draft pointer so an abandoned (unsent) reply/AI draft
    // can't leak into the next composer session opened from elsewhere.
    setDraftMode('none');
    resetViewToMainContent();
  };

  // Post-send close — the composer already deleted its draft and cleared its
  // state, so close UNCONDITIONALLY. Never route this through the unsaved-changes
  // guard: the terminal composer's checkForUnsavedChanges reads a stale render
  // closure immediately after send (state resets are still batched) and would
  // wrongly report unsaved changes, leaving the composer stuck open behind a
  // "Sent ✓" toast.
  const handleComposerSentClose = () => {
    setIsComposerColumnVisible(false);
    setComposerDataForForm(null);
    setDraftMode('none');
    resetViewToMainContent();
  };
  
  const handleOpenReply = () => {
    if (mainContentComponent === 'viewEmail' && mainContentData) {
        // The composer gets the ORIGINAL email and derives the recipients
        // itself (see buildReplyData) - never pre-compute `to` here.
        const replyData = buildReplyData(mainContentData, 'reply');
        // Reset a stale 'edit' draft mode first — a leftover from a
        // backdrop-closed draft makes the composer skip its reply init
        // (it gates on draftMode==='edit') and open blank. 'new' also
        // arms the debounce autosave, matching the feed's compose path.
        setDraftMode('new');
        setComposerDataForForm(replyData);
        setIsComposerColumnVisible(true);
        setEmailPanelView('composeEmail', replyData);
    }
  };

  const handleOpenReplyAll = () => {
    if (mainContentComponent === 'viewEmail' && mainContentData) {
      // Original to/cc/from go through untouched: the composer builds To and
      // Cc (minus the owner's own addresses) from them - see buildReplyData.
      const replyAllData = buildReplyData(mainContentData, 'replyAll');
      setDraftMode('new'); // clear stale 'edit' — see handleOpenReply
      setComposerDataForForm(replyAllData);
      setIsComposerColumnVisible(true);
      setEmailPanelView('composeEmail', replyAllData);
    }
  };

  const handleOpenForward = () => {
    if (mainContentComponent === 'viewEmail' && mainContentData) {
        const forwardData = {
            ...withArrayFrom(mainContentData), // composer maps `from` - see withArrayFrom
            isForward: true,
            to: '', // Forward 'to' is usually empty initially
            // The composer's forwarded-message header shows who the original
            // went to — preserve it before `to` is blanked above.
            originalTo: mainContentData.to,
            subject: formatForwardSubject(mainContentData.subject)
        };
        setDraftMode('new'); // clear stale 'edit' — see handleOpenReply
        setComposerDataForForm(forwardData);
        setIsComposerColumnVisible(true);
        setEmailPanelView('composeEmail', forwardData); // Update global panel view
    }
  };

  const getPanelTitle = () => {
    // Title for the main panel header, might need to be more generic now
    if (isComposerColumnVisible) {
      if (mainContentComponent === 'feed') return "Email Feed & Composer";
      if (mainContentComponent === 'viewEmail') return "View & Compose";
    }

    switch (emailPanelView) {
      case 'drafts':
        return 'Email Drafts';
      case 'editDraft':
        return 'Edit Draft';
      case 'viewEmail':
        return mainContentData?.subject || 'View Email';
      case 'feed':
      default:
        return 'Email Feed';
    }
  };

  return (
    <>
      {inlineStyles}
      {/* Backdrop — must respect the composer's unsaved-changes guard: a stray
          click outside mid-reply previously discarded up to 3s of un-autosaved
          typing with no warning. */}
      <div
        className={`email-panel-backdrop${isEmailPanelOpen ? '' : ' closed'}`}
        onClick={() => {
          // Same rule as handleCloseComposer: only the classic composer blocks
          // the close (it shows its own modal). The terminal composer autosaves,
          // so a backdrop click just closes it — the draft is safe in Drafts.
          if (isComposerColumnVisible && !useTerminalComponents) {
            const activeRef = composerFormRef.current;
            if (activeRef && activeRef.checkForUnsavedChanges()) return; // classic shows its modal
          }
          closeEmailPanel();
        }}
      />
      {/* Sliding Panel */}
      <div
        className={`email-sliding-panel bg-body${isActuallyOpen ? ' open' : ' closed'}${isComposerColumnVisible ? ' composer-open' : ''}${mainContentComponent === 'feed' ? ' feed-view' : ''}`}
      >
        {/* Panel Header */}
        <div
          className="d-flex justify-content-between align-items-center border-bottom panel-main-header"
          style={useTerminalComponents
            ? { minHeight: '36px', padding: '4px 12px', flexShrink: 0 }   // dense terminal/graphite chrome
            : { minHeight: '44px', padding: '8px 20px', flexShrink: 0 }}  // classic keeps Metronic spacing
        >
          {/* text-truncate + minWidth 0: a long subject used to wrap to 2-3
              lines at 375px and shove the action buttons around. */}
          <h5 className="mb-0 fw-bold text-body text-truncate" style={{ flex: 1, minWidth: 0, marginRight: 8 }}>{getPanelTitle()}</h5>
          <div style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {/* Reply, Reply All, and Forward buttons: Only when viewing an email */}
            {mainContentComponent === 'viewEmail' && (
              <>
                <button type='button' className='btn btn-sm btn-icon btn-light-primary me-2' title='Reply' onClick={handleOpenReply}>
                  <ReplyIcon className='svg-icon-2' />
                </button>
                <button type='button' className='btn btn-sm btn-icon btn-light-primary me-2' title='Reply All' onClick={handleOpenReplyAll}>
                  <ReplyAllIcon className='svg-icon-2' />
                </button>
                <button type='button' className='btn btn-sm btn-icon btn-light-primary me-2' title='Forward' onClick={handleOpenForward}>
                  <ForwardIcon className='svg-icon-2' />
                </button>
                {/* More actions — same junk/delete/flag/unread controls as the
                    feed's per-row 3-dot menu, now available on the OPEN email. */}
                <div ref={actionMenuRef} style={{ position: 'relative', display: 'inline-block' }} className='me-2'>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light-primary'
                    title='More actions'
                    aria-haspopup='true'
                    aria-expanded={actionMenuOpen}
                    onClick={() => setActionMenuOpen((v) => !v)}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 700 }}>⋮</span>
                  </button>
                  {actionMenuOpen && (
                    <div
                      role='menu'
                      className='shadow-sm bg-body rounded border panel-viewed-menu'
                      style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, minWidth: 180, zIndex: 1100, padding: '6px 0' }}
                    >
                      <button type='button' role='menuitem' className='btn btn-sm btn-active-light w-100 text-start px-4 py-2' disabled={actionBusy} onClick={handleViewedMarkJunk}>
                        <i className='bi bi-shield-exclamation me-2'></i>Mark as junk
                      </button>
                      <button type='button' role='menuitem' className='btn btn-sm btn-active-light w-100 text-start px-4 py-2' disabled={actionBusy} onClick={handleViewedToggleFlag}>
                        <i className='bi bi-flag me-2'></i>{(mainContentData as any)?.pinned ? 'Unflag' : 'Flag'}
                      </button>
                      <button type='button' role='menuitem' className='btn btn-sm btn-active-light w-100 text-start px-4 py-2' disabled={actionBusy} onClick={handleViewedMarkUnread}>
                        <i className='bi bi-envelope me-2'></i>Mark unread
                      </button>
                      <div className='border-top my-1'></div>
                      <button type='button' role='menuitem' className='btn btn-sm btn-active-light-danger text-danger w-100 text-start px-4 py-2' disabled={actionBusy} onClick={handleViewedDelete}>
                        <i className='bi bi-trash me-2'></i>Delete
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            {/* Back to Feed button: when viewing an email or drafts list */}
            {(mainContentComponent === 'viewEmail' || mainContentComponent === 'drafts') && (
              <button type='button' className='btn btn-sm btn-icon btn-light me-2' title='Back to Feed' onClick={() => { 
                  setMainContentComponent('feed'); 
                  setMainContentData(null); 
                  setEmailPanelView('feed');
                }}>
                <KTSVG path='/media/icons/duotune/arrows/arr019.svg' className='svg-icon-2' />
              </button>
            )}
            {/* Main Panel Close button logic */}
            {!isComposerColumnVisible && (
              <button 
                  type='button' 
                  className='btn btn-sm btn-icon btn-active-icon-primary' 
                  onClick={closeEmailPanel} 
                  title='Close Panel'
              >
                  <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </button>
            )}
          </div>
        </div>

        {/* Push opt-in strip. A sibling ABOVE the body (not inside the feed) so it
            only takes height while it renders; feed view only, and never while
            the composer column is open or still animating out. */}
        {mainContentComponent === 'feed' && !isComposerColumnVisible && !shouldRenderComposer && (
          <PushOptIn variant={isGraphite ? 'graphite' : isTerminalTheme ? 'terminal' : 'classic'} />
        )}

        {/* Panel Body with two columns */}
        <div className="panel-body-flex">
          {/* Composer Side Panel (slides in from the right, never overlaps) */}
          {shouldRenderComposer && (
            <div
              className={`composer-panel-side bg-body${isComposerColumnVisible ? ' open' : ' closed'}`}
            >
              <div
                className="d-flex justify-content-between align-items-center border-bottom"
                style={useTerminalComponents
                  ? { padding: '4px 12px', minHeight: '32px', flexShrink: 0 }
                  : { padding: '8px 20px', flexShrink: 0 }}
              >
                <h6 className="mb-0 fw-bold text-body" style={useTerminalComponents ? { fontSize: '12px' } : undefined}>
                  {composerDataForForm?.isReplyAll ? 'Reply All' : composerDataForForm?.isReply ? 'Reply' : composerDataForForm?.isForward ? 'Forward' : 'Compose Email'}
                </h6>
                <button type='button' className='btn btn-sm btn-icon btn-active-icon-primary' onClick={handleCloseComposer} title='Close Composer'>
                  <KTSVG path='/media/icons/duotune/arrows/arr088.svg' className='svg-icon-3' />
                </button>
              </div>
              {/* No safe-area padding here — the composer's own sticky bottom
                  bar carries it on phones (padding twice left a dead gap). */}
              <div style={{flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0, width: '100%', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch'}}>
                {shouldRenderComposerContent ? (
                  // Terminal theme gets the Tiptap-based TerminalSendEmailForm
                  // (same one the job workspace uses). Classic theme keeps
                  // the CKEditor-based SendEmailForm. Both share the same
                  // draft collection + send cloud function.
                  useTerminalComponents ? (
                    <TerminalSendEmailForm
                      ref={terminalComposerFormRef}
                      initialData={composerDataForForm}
                      // Opened FROM a saved draft: it hydrates even when the view data carries
                      // reply / forward flags (a duplicated reply draft does) instead of
                      // opening blank.
                      resumeDraftId={emailPanelView === 'editDraft' ? currentDraft?.id : undefined}
                      onSendSuccess={handleComposerSentClose}
                    />
                  ) : (
                    <SendEmailForm
                      ref={composerFormRef}
                      initialData={composerDataForForm}
                      onSendSuccess={handleComposerSentClose}
                    />
                  )
                ) : (
                  <div className="p-5 text-center text-muted">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Loading...
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Feed/Detail Panel (always visible, fixed width) */}
          <div className={`feed-panel${isComposerColumnVisible ? ' composer-open' : ''}`}>
            {mainContentComponent === 'feed' && (useTerminalComponents ? <TerminalEmailFeed /> : <EmailFeedContent />)}
            {mainContentComponent === 'viewEmail' && (useTerminalComponents
              ? <TerminalEmailView emailData={mainContentData} />
              : <EmailView emailData={mainContentData} />
            )}
            {mainContentComponent === 'drafts' && <DraftsPanel />}
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailSlidingPanel;
